import { useCallback } from 'react'
import { CheckCircle, Calendar, ChevronsUpDown, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { SearchableCombobox } from '@/components/ui/searchable-combobox'
import { calculateFollowUpDate } from '@/lib/dates'
import { dermDiagnoses, type DermDiagnosis } from '@/data/derm-diagnoses'
import PrescriptionPanel from './PrescriptionPanel'
import SkincarePanel from './SkincarePanel'
import type {
  TreatmentPlan,
  Prescription,
  FollowUpWeeks,
  DocumentationAction,
} from '@/types/documentation'

interface TreatmentPlanEditorProps {
  treatmentPlan: TreatmentPlan
  treatmentPlanApproved: boolean
  dispatch: React.Dispatch<DocumentationAction>
  prescriptions: Prescription[]
  followUpWeeks: FollowUpWeeks
  followUpDate: string | null
}

const FOLLOW_UP_OPTIONS: { value: FollowUpWeeks; label: string }[] = [
  { value: 2, label: '2 weeks' },
  { value: 4, label: '4 weeks' },
  { value: 6, label: '6 weeks' },
  { value: 8, label: '8 weeks' },
  { value: 12, label: '12 weeks' },
  { value: 'custom', label: 'Custom date' },
  { value: 'none', label: 'No follow-up' },
]

const WEEKS_MAP: Record<string, FollowUpWeeks> = {
  '2': 2, '4': 4, '6': 6, '8': 8, '12': 12,
  'custom': 'custom', 'none': 'none',
}

const diagnosisFilterFn = (d: DermDiagnosis, q: string) =>
  d.label.toLowerCase().includes(q) || d.category.toLowerCase().includes(q)

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
      {children}
    </p>
  )
}

function DiagnosisSelector({
  selectedDiagnosis,
  onSelect,
  onClear,
}: {
  selectedDiagnosis: string | null
  onSelect: (d: DermDiagnosis) => void
  onClear: () => void
}) {
  const renderItem = useCallback(
    (d: DermDiagnosis) => (
      <span className="text-xs text-slate-700">{d.label}</span>
    ),
    [],
  )

  if (selectedDiagnosis) {
    return (
      <div className="flex items-center gap-2">
        <Badge variant="secondary" className="gap-1 text-sm font-normal">
          {selectedDiagnosis}
          <button
            onClick={onClear}
            className="ml-0.5 hover:text-red-500"
            aria-label="Clear diagnosis"
          >
            <X className="h-3 w-3" />
          </button>
        </Badge>
      </div>
    )
  }

  return (
    <SearchableCombobox
      items={dermDiagnoses}
      filterFn={diagnosisFilterFn}
      onSelect={onSelect}
      renderItem={renderItem}
      trigger={
        <Button variant="outline" size="sm" className="w-full justify-between text-xs text-slate-500 font-normal">
          Select a diagnosis...
          <ChevronsUpDown className="h-3 w-3 shrink-0 opacity-50" />
        </Button>
      }
      placeholder="Search diagnoses..."
      searchLabel="Search diagnoses"
      resultsLabel="Diagnosis search results"
      emptyText="No diagnoses found"
      popoverWidth="w-[350px]"
      scrollHeight="h-[280px]"
      showSearchIcon
      limit={30}
    />
  )
}

export default function TreatmentPlanEditor({
  treatmentPlan,
  treatmentPlanApproved,
  dispatch,
  prescriptions,
  followUpWeeks,
  followUpDate,
}: TreatmentPlanEditorProps) {
  const updatePlan = (updates: Partial<TreatmentPlan>) => {
    dispatch({
      type: 'SET_TREATMENT_PLAN',
      plan: { ...treatmentPlan, ...updates },
    })
  }

  return (
    <div className="space-y-4">
      {/* Status bar */}
      {treatmentPlanApproved && (
        <div className="flex items-center gap-2">
          <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
            <CheckCircle className="h-3 w-3 mr-1" />
            Approved
          </Badge>
        </div>
      )}

      {/* Diagnosis */}
      <div className="rounded-lg border bg-white p-4 space-y-2">
        <SectionLabel>Diagnosis</SectionLabel>
        {!treatmentPlanApproved ? (
          <div className="space-y-2">
            <DiagnosisSelector
              selectedDiagnosis={treatmentPlan.selected_diagnosis}
              onSelect={(d) => updatePlan({ selected_diagnosis: d.label })}
              onClear={() => updatePlan({ selected_diagnosis: null })}
            />
            <Input
              value={treatmentPlan.diagnosis}
              onChange={(e) => updatePlan({ diagnosis: e.target.value })}
              placeholder="Additional details (optional)"
              aria-label="Diagnosis details"
              className="text-sm"
            />
          </div>
        ) : (
          <div className="space-y-1">
            {treatmentPlan.selected_diagnosis && (
              <p className="text-sm font-medium text-slate-900">{treatmentPlan.selected_diagnosis}</p>
            )}
            {treatmentPlan.diagnosis && (
              <p className="text-sm text-slate-700">{treatmentPlan.diagnosis}</p>
            )}
          </div>
        )}
      </div>

      {/* Prescriptions */}
      <div className="rounded-lg border bg-white p-4 space-y-2">
        <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
          Prescriptions
        </p>
        <PrescriptionPanel
          prescriptions={prescriptions}
          dispatch={dispatch}
          embedded
        />
      </div>

      {/* Skincare */}
      <div className="rounded-lg border bg-white p-4 space-y-2">
        <SectionLabel>Skincare Recommendations</SectionLabel>
        <SkincarePanel
          skincare={treatmentPlan.skincare}
          onAdd={(rec) =>
            updatePlan({ skincare: [...treatmentPlan.skincare, rec] })
          }
          onUpdate={(index, rec) => {
            const recs = [...treatmentPlan.skincare]
            recs[index] = rec
            updatePlan({ skincare: recs })
          }}
          onRemove={(index) =>
            updatePlan({
              skincare: treatmentPlan.skincare.filter((_, j) => j !== index),
            })
          }
          disabled={treatmentPlanApproved}
        />
      </div>

      {/* Take-Home Instructions */}
      <div className="rounded-lg border bg-white p-4 space-y-2">
        <SectionLabel>Take-Home Instructions</SectionLabel>
        {!treatmentPlanApproved ? (
          <Textarea
            value={treatmentPlan.take_home_instructions}
            onChange={(e) => updatePlan({ take_home_instructions: e.target.value })}
            aria-label="Take-home instructions"
            placeholder="Enter take-home instructions for the patient..."
            rows={4}
            className="text-sm"
          />
        ) : (
          <p className="text-sm text-slate-700 whitespace-pre-wrap">
            {treatmentPlan.take_home_instructions}
          </p>
        )}
      </div>

      {/* Follow-Up */}
      <div className="rounded-lg border bg-white p-4 space-y-2">
        <SectionLabel>Follow-Up</SectionLabel>
        {!treatmentPlanApproved ? (
          <div className="space-y-2">
            <Select
              value={String(followUpWeeks)}
              onValueChange={(value) => {
                const weeks = WEEKS_MAP[value] ?? 'none'
                dispatch({ type: 'SET_FOLLOW_UP', weeks })
              }}
            >
              <SelectTrigger aria-label="Follow-up interval">
                <SelectValue placeholder="Select follow-up interval" />
              </SelectTrigger>
              <SelectContent>
                {FOLLOW_UP_OPTIONS.map((opt) => (
                  <SelectItem key={String(opt.value)} value={String(opt.value)}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {followUpWeeks === 'custom' && (
              <Input
                type="date"
                value={followUpDate ?? ''}
                onChange={(e) =>
                  dispatch({ type: 'SET_FOLLOW_UP', weeks: 'custom', date: e.target.value })
                }
                min={new Date().toLocaleDateString('en-CA')}
                aria-label="Custom follow-up date"
              />
            )}
          </div>
        ) : (
          <div className="flex items-center gap-2 text-sm text-slate-700">
            <Calendar className="h-4 w-4 text-slate-400" />
            {followUpWeeks === 'none' ? (
              <span className="text-slate-400">No follow-up set</span>
            ) : typeof followUpWeeks === 'number' ? (
              <span>{followUpWeeks} weeks · {calculateFollowUpDate(followUpWeeks)}</span>
            ) : followUpDate ? (
              <span>
                {new Date(followUpDate).toLocaleDateString([], {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </span>
            ) : (
              <span className="text-slate-400">Custom date not set</span>
            )}
          </div>
        )}
      </div>

      {!treatmentPlanApproved && (
        <Button
          size="sm"
          onClick={() => dispatch({ type: 'APPROVE_TREATMENT_PLAN' })}
        >
          <CheckCircle className="h-4 w-4 mr-1" />
          Approve Treatment Plan
        </Button>
      )}
    </div>
  )
}
